import { showsNewBadge } from './newLessonsSeen';
import type { Requirement } from '../components/course-versioning/learnerWhatsNew';

/**
 * What a lesson row shows: a New or Updated pill, and — for a lesson a publish
 * changed — whether this learner is Required to do it or it's Optional.
 *
 * Course versioning stores `badge` ('new' | 'updated') and `pendingAction` on
 * the learner's record (written by a publish, cleared by the BE when the
 * lesson is completed again). A pending action that isn't `optional` makes the
 * lesson Required; any other lesson a publish badged is Optional (a completer
 * who's exempt from a new lesson, or an update with nothing to redo). The
 * B4-5 `isNew` rule stays as the fallback and shows New with no tag; the BE
 * reports it as badge 'new' too, and `badgeSource` says which one produced it.
 *
 * The localStorage seen-set applies only to the fallback: `badgeSource`
 * 'fallback', or undefined on an older BE build (there a stored 'new' on a
 * lesson the fallback would not flag still shows, and when both are true
 * the two are indistinguishable and the seen-set wins, as before
 * versioning). A 'stored' badge is never hidden by the seen-set: the BE
 * clears it when the learner completes the lesson.
 */
export interface LessonPillState {
  kind: 'new' | 'updated';
  /** Null for the B4-5 fallback New, which no publish decided on. */
  requirement: Requirement | null;
}
/** 'stored' = written by a publish (group `badges` map / task `badge`), 'fallback' = only the B4-5 isNew rule, null = no badge. */
export type LessonBadgeSource = 'stored' | 'fallback' | null;

export interface LessonBadgeInput {
  badge?: 'new' | 'updated' | null;
  /** Absent on older BE builds: treated like 'fallback' for the seen-set. */
  badgeSource?: LessonBadgeSource;
  pendingAction?: { kind?: string; optional?: boolean } | null;
  isNew?: boolean;
  complete: boolean;
  seen: Set<string>;
  taskId: string;
}

export const resolveLessonPill = ({
  badge,
  badgeSource,
  pendingAction,
  isNew,
  complete,
  seen,
  taskId
}: LessonBadgeInput): LessonPillState | null => {
  const requirement: Requirement = pendingAction && pendingAction.optional !== true ? 'required' : 'optional';
  if (pendingAction) {
    return { kind: pendingAction.kind === 'new' || badge === 'new' ? 'new' : 'updated', requirement };
  }
  if (badge === 'updated') return { kind: 'updated', requirement };
  if (complete) return null;
  // Written by a publish: the BE's to clear, never the seen-set's.
  if (badge === 'new' && badgeSource === 'stored') return { kind: 'new', requirement };
  // Older BE (no badgeSource): a stored 'new' the fallback would not flag still shows.
  if (badge === 'new' && badgeSource === undefined && !isNew) return { kind: 'new', requirement };
  // Fallback (B4-5 isNew, mirrored as badge 'new'): cleared locally once opened.
  if (showsNewBadge(Boolean(isNew) || badge === 'new', complete, seen, taskId)) {
    return { kind: 'new', requirement: null };
  }
  return null;
};
